<template>
    <v-slide-x-transition mode="out-in">
        <v-container v-if="showComponent" style="max-width: 600px">
            <CardTitle
                :title="$t('login.title')"
                :link="{ href: '/register', text: $t('login.noAccount') }"
            />
            <v-card class="pa-5 mt-2">
                <v-card-text>
                    <v-form @submit.prevent="handleLogin">
                        <v-btn
                            data-cy="login-google"
                            :loading="googleLoading"
                            :disabled="googleLoading"
                            @click="handleGoogleLogin"
                            block
                        >
                            {{ $t("login.signInWithGoogle") }}
                        </v-btn>
                        <v-divider>{{ $t("login.or") }}</v-divider>
                        <v-text-field
                            data-cy="login-email"
                            v-model="email"
                            :label="$t('login.email')"
                            autocomplete="email"
                            required
                        ></v-text-field>
                        <v-text-field
                            data-cy="login-password"
                            v-model="password"
                            :label="$t('login.password')"
                            type="password"
                            autocomplete="current-password"
                            required
                        ></v-text-field>
                        <TextLink
                            :link="{
                                href: '/request-reset-password',
                                text: $t('login.forgotPassword'),
                            }"
                        />
                    </v-form>
                </v-card-text>
                <v-card-actions class="justify-center">
                    <v-btn
                        data-cy="login-submit"
                        variant="flat"
                        :loading="emailPasswordLoading"
                        :disabled="emailPasswordLoading"
                        @click="handleLogin"
                    >
                        {{ $t("login.login") }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-container>
    </v-slide-x-transition>
</template>

<script>
import axios from "axios";
import { mapActions } from "vuex";
import { GoogleAuthProvider, signInWithPopup, signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "@/firebase/config";
import LanguageSelector from "@/components/LanguageSelector.vue";
import CardTitle from "@/components/registration/CardTitle.vue";
import {
    showAlertModal,
    startSessionWatcher,
} from "@/utils";
import TextLink from "@/components/registration/TextLink.vue";
import { clearLogoutFlag } from "@/api";

export default {
    components: { TextLink, LanguageSelector, CardTitle },
    mounted() {
        this.showComponent = true;
    },
    data() {
        return {
            showComponent: false,
            email: "",
            password: "",
            googleLoading: false,
            emailPasswordLoading: false,
        };
    },
    methods: {
        ...mapActions("auth", ["login", "firebaseLogin"]),
        showAlertModal,
        async handleGoogleLogin() {
            this.googleLoading = true;
            clearLogoutFlag();
            try {
                const provider = new GoogleAuthProvider();
                const result = await signInWithPopup(auth, provider);
                const idToken = await result.user.getIdToken();
                const response = await this.firebaseLogin(idToken);

                /* ---------- success ---------- */
                if (response?.access) {
                    if (response.user) {
                        sessionStorage.setItem(
                            "user",
                            JSON.stringify(response.user)
                        );
                        startSessionWatcher();
                    }
                    this.$router.replace(
                        this.$route.query.redirect || "/dashboard"
                    );
                    return;
                }

                /* ---------- failure ---------- */
                console.error("Google login error:", response);
                const data = response?.response?.data;
                const message =
                    data?.error || this.$t("common.unexpectedError");
                this.showAlertModal(this.$store, message, "danger", 12000, response);
            } catch (err) {
                if (err?.code !== "auth/popup-closed-by-user") {
                    this.showAlertModal(
                        this.$store,
                        err?.message || this.$t("common.unexpectedError"),
                        "danger",
                        12000,
                        err
                    );
                }
            } finally {
                this.googleLoading = false;
            }
        },
        async handleLogin() {
            clearLogoutFlag();
            this.emailPasswordLoading = true;

            try {
                const { data: providerData } = await axios.post(
                    `${process.env.VUE_APP_API_URL}/auth/auth-provider-by-email/`,
                    {
                        email: this.email,
                    }
                );

                /* ---------- unknown email ---------- */
                if (providerData.provider === null) {
                    this.showAlertModal(
                        this.$store,
                        this.$t("login.invalid"),
                        "danger",
                        12000
                    );
                    return;
                }

                /* ---------- Firebase (email/password) ---------- */
                if (providerData.provider === "firebase") {
                    const result = await signInWithEmailAndPassword(
                        auth,
                        providerData.email,
                        this.password
                    );
                    const idToken = await result.user.getIdToken(true);
                    const response = await this.firebaseLogin(idToken);

                    /* ---------- success ---------- */
                    if (response?.access) {
                        if (response.user) {
                            sessionStorage.setItem(
                                "user",
                                JSON.stringify(response.user)
                            );
                            startSessionWatcher();
                        }
                        this.$router.replace(
                            this.$route.query.redirect || "/dashboard"
                        );
                        return;
                    }

                    /* ---------- failure ---------- */
                    console.error("Firebase login error:", response);
                    const errData = response?.response?.data;
                    let message = errData?.error || this.$t("login.invalid");
                    /* company inactive (403) ----------------------------------- */
                    if (
                        response?.response?.status === 403 &&
                        errData?.error?.includes("company's account is disabled")
                    ) {
                        message = this.$t("login.companyInactive");
                    } else if (
                        response?.response?.status === 403 &&
                        (errData?.error?.toLowerCase().includes("pending") ||
                            errData?.error?.toLowerCase().includes("approval"))
                    ) {
                        message = this.$t("login.pendingApproval");
                    }
                    this.showAlertModal(this.$store, message, "danger", 12000, response);
                    return;
                }

                /* ---------- Django (legacy) ---------- */
                if (providerData.provider === "django") {
                    const response = await this.login({
                        email: this.email,
                        password: this.password,
                    });

                    /* ---------- success ---------- */
                    if (response?.access) {
                        if (response.user) {
                            sessionStorage.setItem(
                                "user",
                                JSON.stringify(response.user)
                            );
                            startSessionWatcher();
                        }
                        this.$router.replace(
                            this.$route.query.redirect || "/dashboard"
                        );
                        return;
                    }

                    /* ---------- failure ---------- */
                    console.error("Login error:", response);
                    let message = this.$t("login.invalid");
                    if (response?.response) {
                        const { status, data } = response.response;
                        /* company inactive (403) ----------------------------------- */
                        if (
                            status === 403 &&
                            data?.error?.includes("company's account is disabled")
                        ) {
                            message = this.$t("login.companyInactive");
                            /* invalid credentials (400) -------------------------------- */
                        } else if (status === 400 && data?.error) {
                            message = this.$t("login.invalid");
                            /* any other structured error ------------------------------ */
                        } else if (data?.detail) {
                            message = data.detail;
                        }
                    }
                    this.showAlertModal(
                        this.$store,
                        message,
                        "danger",
                        12000,
                        response
                    );
                }
            } catch (err) {
                /* invalid credentials (Firebase) -------------------------------- */
                if (
                    err?.code === "auth/invalid-credential" ||
                    err?.code === "auth/wrong-password" ||
                    err?.code === "auth/user-not-found"
                ) {
                    this.showAlertModal(
                        this.$store,
                        this.$t("login.invalid"),
                        "danger",
                        12000
                    );
                } else {
                    this.showAlertModal(
                        this.$store,
                        err?.message || this.$t("common.unexpectedError"),
                        "danger",
                        12000,
                        err
                    );
                }
            } finally {
                this.emailPasswordLoading = false;
            }
        },
    },
};
</script>